import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Calculator, CreditCard, Crown, Package, Plus, ShieldCheck, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import MobileMoneyPayment from '@/components/shared/MobileMoneyPayment';
import DeliveryEstimateSummary from '@/components/shared/DeliveryEstimateSummary';
import { API, toast } from '@/App';
import { logClientError } from '@/utils/clientError';
import {
  buildDeliveryItemsPayload,
  buildDeliveryItemsSummary,
  createEmptyDeliveryItem,
  hydrateDeliveryItemDrafts,
} from '@/utils/deliveryItems';
import { MOBILE_MONEY_METHOD_IDS } from '@/utils/paymentMethodAvailability';
import { uploadPaymentProof } from '@/utils/paymentProofUpload';
import { buildDeliveryPromoQuote, getPromoFeedbackMessage, normalizePromoCodeInput } from '@/utils/promoCodes';

export const resolvePaymentMutationMessage = (payload, fallbackMessage) => {
  const candidate = typeof payload?.message === 'string' ? payload.message.trim() : '';
  return candidate || fallbackMessage;
};

export const isAlreadyProcessedMutation = (payload) => Boolean(payload?.already_processed || payload?.idempotent_replay);
export const DELIVERY_HISTORY_ROUTE = '/vendeur/history';

export const extractCreateDeliveryId = (payload) => {
  const directId = typeof payload?.id === 'string' ? payload.id.trim() : '';
  if (directId) {
    return directId;
  }

  const nestedId = typeof payload?.data?.id === 'string' ? payload.data.id.trim() : '';
  if (nestedId) {
    return nestedId;
  }

  const nestedDeliveryId = typeof payload?.data?.delivery?.id === 'string' ? payload.data.delivery.id.trim() : '';
  if (nestedDeliveryId) {
    return nestedDeliveryId;
  }

  return '';
};

const buildDeliveryCreateIdempotencyKey = () => {
  if (typeof globalThis?.crypto?.randomUUID === 'function') {
    return `delivery-create:${globalThis.crypto.randomUUID()}`;
  }
  return `delivery-create:${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const DELIVERY_PAYMENT_ALREADY_HANDLED_DETAILS = new Set([
  'Delivery already paid',
  'PAYMENT_ALREADY_PENDING_VALIDATION',
]);

const DELIVERY_PAYMENT_ALREADY_PROCESSING_DETAILS = new Set([
  'PAYMENT_ALREADY_PROCESSING',
]);

export const isDeliveryPaymentAlreadyHandledDetail = (detail) => DELIVERY_PAYMENT_ALREADY_HANDLED_DETAILS.has(String(detail || '').trim());

export const isDeliveryPaymentProcessingDetail = (detail) => DELIVERY_PAYMENT_ALREADY_PROCESSING_DETAILS.has(String(detail || '').trim());

export const resolveCreateDeliveryFailureMessage = ({ detail, hasCreatedDeliveryId }) => {
  if (hasCreatedDeliveryId) {
    return "La livraison a ete creee, mais le paiement n'a pas pu etre finalise. Vous pouvez reessayer sans recreer la commande.";
  }
  return getPromoFeedbackMessage(detail, detail || 'Erreur lors de la création');
};

const createInitialFormData = () => ({
  client_name: '',
  client_phone: '',
  client_address: '',
  delivery_reference: '',
  client_district: '',
  client_city: '',
  pickup_address: '',
  pickup_reference: '',
  pickup_district: '',
  pickup_city: '',
  weight: '',
  description: '',
  items: hydrateDeliveryItemDrafts(),
  service_tier: 'standard',
  is_insured: false,
  insured_value: '',
  duplicate_source_delivery_id: '',
});

const CreateDelivery = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateStateRef = useRef(null);
  const defaultPickupAppliedRef = useRef(false);
  const submitLockRef = useRef(false);
  const createdDeliveryIdRef = useRef('');
  const deliveryCreateIdempotencyKeyRef = useRef(buildDeliveryCreateIdempotencyKey());
  const [calculating, setCalculating] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [estimatedCost, setEstimatedCost] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [viewerProfile, setViewerProfile] = useState(null);
  const [viewerEntitlements, setViewerEntitlements] = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);
  const [duplicationContext, setDuplicationContext] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedAddressesLoading, setSavedAddressesLoading] = useState(false);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState('');
  const [savePickupAddress, setSavePickupAddress] = useState(false);
  const [savePickupAsDefault, setSavePickupAsDefault] = useState(false);
  const [pickupAddressLabel, setPickupAddressLabel] = useState('');
  const [formData, setFormData] = useState(() => createInitialFormData());
  const productSummary = buildDeliveryItemsSummary(formData.items);
  const hasDetailedItems = productSummary.hasDetailedItems;
  const usesComputedWeight = productSummary.hasCompleteWeightData && productSummary.totalWeight > 0;
  const effectiveDeliveryWeight = usesComputedWeight
    ? productSummary.totalWeight
    : (formData.weight ? Number(formData.weight) : null);
  const effectiveDeclaredValue = hasDetailedItems && productSummary.totalDeclaredValue > 0
    ? productSummary.totalDeclaredValue
    : undefined;
  const displayedWeightValue = usesComputedWeight ? String(productSummary.totalWeight) : formData.weight;

  useEffect(() => {
    loadCountries();
    loadSellerContext();
    loadWalletSummary();
    loadSavedAddresses();
  }, []);

  useEffect(() => {
    if (viewerEntitlements === null) {
      return;
    }
    const isProSeller = Boolean(viewerEntitlements?.is_pro_active || viewerEntitlements?.isProActive);
    if (isProSeller) {
      return;
    }
    if (formData.service_tier === 'express' || formData.is_insured) {
      setFormData((prev) => ({
        ...prev,
        service_tier: 'standard',
        is_insured: false,
        insured_value: '',
      }));
      setEstimatedCost(null);
      setPromoValidation(null);
    }
  }, [formData.service_tier, formData.is_insured, viewerEntitlements]);

  useEffect(() => {
    const duplicatePayload = location.state?.deliveryDuplicate;
    const prefill = duplicatePayload?.prefill;
    const sourceId = prefill?.duplicate_source_delivery_id || duplicatePayload?.source_delivery?.id;

    if (!duplicatePayload || !prefill || !sourceId) {
      return;
    }
    if (duplicateStateRef.current === sourceId) {
      return;
    }

    duplicateStateRef.current = sourceId;
    setFormData((prev) => ({
      ...prev,
      ...prefill,
      items: hydrateDeliveryItemDrafts(prefill.items),
      is_insured: Boolean(prefill.is_insured),
      insured_value: prefill.insured_value ?? '',
      duplicate_source_delivery_id: sourceId,
    }));
    setDuplicationContext({
      ...duplicatePayload,
      sourceId,
    });
    setEstimatedCost(null);
    setPaymentMethod('');
    setTransactionId('');
    setPaymentProofFile(null);
  }, [location.state]);

  useEffect(() => {
    if (defaultPickupAppliedRef.current) {
      return;
    }
    if (location.state?.deliveryDuplicate?.prefill || duplicationContext) {
      return;
    }
    if (formData.pickup_address || formData.pickup_reference || formData.pickup_city || formData.pickup_district) {
      return;
    }
    const defaultAddress = savedAddresses.find((item) => item.address_type === 'pickup' && item.is_default);
    if (!defaultAddress) {
      return;
    }
    defaultPickupAppliedRef.current = true;
    applySavedPickupAddress(defaultAddress);
  }, [
    duplicationContext,
    formData.pickup_address,
    formData.pickup_reference,
    formData.pickup_city,
    formData.pickup_district,
    location.state,
    savedAddresses,
  ]);

  const loadCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      const activeCountries = (response.data || []).filter((country) => country.is_active);
      setAllCities(
        activeCountries.flatMap((country) =>
          (country.cities || []).map((city) => ({
            ...city,
            country_code: country.code,
            country_name: country.name,
          }))
        )
      );
    } catch (error) {
      logClientError('create_delivery.load_cities', error);
      toast.error('Erreur lors du chargement des villes');
    }
  };

  const loadSellerContext = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setViewerProfile(response.data || null);
      setViewerEntitlements(response.data?.entitlements || null);
    } catch (error) {
      logClientError('create_delivery.load_seller_context', error);
      setViewerProfile(null);
      setViewerEntitlements(null);
    }
  };

  const loadWalletSummary = async () => {
    try {
      const response = await axios.get(`${API}/wallet`);
      setWalletSummary(response.data || null);
    } catch (error) {
      setWalletSummary(null);
    }
  };

  const loadSavedAddresses = async () => {
    setSavedAddressesLoading(true);
    try {
      const response = await axios.get(`${API}/user/addresses`, {
        params: { address_type: 'pickup' },
      });
      setSavedAddresses(response.data?.data?.items || []);
    } catch (error) {
      logClientError('create_delivery.load_saved_addresses', error);
      setSavedAddresses([]);
    } finally {
      setSavedAddressesLoading(false);
    }
  };

  const resetAppliedPromo = () => {
    setPromoValidation(null);
    setEstimatedCost((prev) => {
      if (!prev) {
        return prev;
      }
      const amountBeforeDiscount = Number(prev.amountBeforeDiscount ?? prev.amount ?? 0);
      return {
        ...prev,
        amount: amountBeforeDiscount,
        amountBeforeDiscount,
        amountAfterDiscount: amountBeforeDiscount,
        discountAmount: 0,
        hasDiscount: false,
        promoCode: null,
        promoLabel: null,
      };
    });
  };

  const handleInputChange = (event) => {
    if (['pickup_address', 'pickup_reference', 'pickup_district'].includes(event.target.name)) {
      setSelectedSavedAddressId('');
    }
    setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    setEstimatedCost(null);
    resetAppliedPromo();
  };

  const handleSelectChange = (name, value) => {
    if (['pickup_city', 'pickup_district'].includes(name)) {
      setSelectedSavedAddressId('');
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setEstimatedCost(null);
    resetAppliedPromo();
  };

  const handleItemChange = (rowId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: (prev.items || []).map((item) => (
        item.row_id === rowId
          ? { ...item, [field]: value }
          : item
      )),
    }));
    setEstimatedCost(null);
    resetAppliedPromo();
  };

  const handleAddProductRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...(prev.items || []), createEmptyDeliveryItem()],
    }));
    setEstimatedCost(null);
    resetAppliedPromo();
  };

  const handleRemoveProductRow = (rowId) => {
    setFormData((prev) => {
      const nextItems = (prev.items || []).filter((item) => item.row_id !== rowId);
      return {
        ...prev,
        items: nextItems.length ? nextItems : [createEmptyDeliveryItem()],
      };
    });
    setEstimatedCost(null);
    resetAppliedPromo();
  };

  const getDistrictsForCity = (cityCode) => {
    const city = allCities.find((item) => item.code === cityCode);
    return city?.districts || [];
  };

  const formatSavedAddressLabel = (address) => {
    if (!address) {
      return '';
    }
    return [
      address.label,
      address.address,
      address.reference ? `Référence : ${address.reference}` : '',
      address.district,
      address.city_name || address.city,
    ].filter(Boolean).join(' â€¢ ');
  };

  const applySavedPickupAddress = (address) => {
    if (!address) {
      return;
    }
    setSelectedSavedAddressId(address.id || '');
    setFormData((prev) => ({
      ...prev,
      pickup_address: address.address || '',
      pickup_reference: address.reference || '',
      pickup_district: address.district || '',
      pickup_city: address.city || '',
    }));
    setEstimatedCost(null);
    resetAppliedPromo();
  };

  const requestEstimate = async ({ silent = false } = {}) => {
    if (!formData.pickup_city || !formData.client_city || !formData.pickup_address || !formData.client_address || !effectiveDeliveryWeight) {
      if (!silent) {
        toast.error('Veuillez remplir les villes, les adresses et le poids pour calculer le cout');
      }
      return false;
    }

    setCalculating(true);
    try {
      const response = await axios.post(`${API}/deliveries/estimate-cost`, {
        pickup_address: formData.pickup_address,
        pickup_city: formData.pickup_city,
        delivery_address: formData.client_address,
        delivery_city: formData.client_city,
        weight: Number(effectiveDeliveryWeight),
        service_tier: formData.service_tier || 'standard',
        is_insured: Boolean(formData.is_insured),
        insured_value: formData.is_insured && formData.insured_value ? parseFloat(formData.insured_value) : undefined,
      });

      const amount = response.data?.prix_total ?? response.data?.price_total;
      const currency = response.data?.devise ?? response.data?.currency;
      if (typeof amount !== 'number' || !currency) {
        throw new Error('INVALID_ESTIMATE_RESPONSE');
      }

      setEstimatedCost({
        amount,
        currency,
        amountBeforeDiscount: amount,
        amountAfterDiscount: amount,
        discountAmount: 0,
        hasDiscount: false,
        promoCode: null,
        promoLabel: null,
        distanceKm: typeof response.data?.distance_km === 'number' ? response.data.distance_km : null,
        detailCalcul: response.data?.detail_calcul || null,
        serviceTier: response.data?.service_tier || formData.service_tier || 'standard',
        isInsured: Boolean(response.data?.is_insured),
        insuredValue: response.data?.insured_value ?? null,
        transportAmount: response.data?.transport_amount ?? amount,
        expressSurchargeAmount: response.data?.express_surcharge_amount ?? 0,
        insuranceAmount: response.data?.insurance_amount ?? 0,
        deliveryEstimate: response.data?.delivery_estimate || null,
      });
      setPromoValidation(null);
      return true;
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || 'Erreur lors du calcul du cout');
      }
      return false;
    } finally {
      setCalculating(false);
    }
  };

  const calculateEstimate = async () => {
    await requestEstimate({ silent: false });
  };

  const handleApplyPromoCode = async () => {
    const normalizedPromoCode = normalizePromoCodeInput(promoCodeInput);
    if (!normalizedPromoCode) {
      toast.error('Saisissez un code promo.');
      return;
    }
    if (!estimatedCost) {
      toast.error('Calculez d abord le cout avant d appliquer un code promo.');
      return;
    }

    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: normalizedPromoCode,
        context_type: 'delivery',
        delivery_quote: buildDeliveryPromoQuote(formData, { weight: effectiveDeliveryWeight }),
      });
      const result = response.data || {};
      if (!result.valid) {
        setPromoValidation(result);
        resetAppliedPromo();
        toast.error(getPromoFeedbackMessage(result.status, result.message));
        return;
      }

      const pricing = result.pricing || {};
      setPromoValidation(result);
      setPromoCodeInput(normalizedPromoCode);
      setEstimatedCost((prev) => ({
        ...(prev || {}),
        amount: Number(pricing.amount_after ?? prev?.amount ?? 0),
        amountBeforeDiscount: Number(pricing.amount_before ?? prev?.amountBeforeDiscount ?? prev?.amount ?? 0),
        amountAfterDiscount: Number(pricing.amount_after ?? prev?.amount ?? 0),
        discountAmount: Number(pricing.discount_amount ?? 0),
        hasDiscount: Boolean(pricing.has_discount || Number(pricing.discount_amount || 0) > 0),
        currency: pricing.currency || prev?.currency || 'FCFA',
        promoCode: result.promo_code?.code || normalizedPromoCode,
        promoLabel: result.promo_code?.label || result.promo_code?.code || normalizedPromoCode,
      }));
      toast.success(result.message || 'Code promo applique.');
    } catch (error) {
      toast.error(getPromoFeedbackMessage(error.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  useEffect(() => {
    if (!duplicationContext) {
      return undefined;
    }
    if (!formData.pickup_city || !formData.client_city || !formData.pickup_address || !formData.client_address || !effectiveDeliveryWeight) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      requestEstimate({ silent: true }).catch(() => undefined);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [
    duplicationContext,
    formData.pickup_city,
    formData.client_city,
    formData.pickup_address,
    formData.client_address,
    formData.weight,
    formData.items,
    formData.service_tier,
    formData.is_insured,
    formData.insured_value,
    effectiveDeliveryWeight,
  ]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (
      !formData.client_name
      || !formData.client_phone
      || !formData.client_city
      || !formData.client_district
      || !formData.client_address
      || !formData.pickup_city
      || !formData.pickup_district
      || !formData.pickup_address
      || !effectiveDeliveryWeight
      || !formData.description
    ) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!estimatedCost) {
      toast.error('Veuillez calculer le coût estimé avant de continuer');
      return;
    }
    setShowPaymentDialog(true);
  };

  const handleCreateDelivery = async () => {
    if (processing || submitLockRef.current) {
      return;
    }
    if (!paymentMethod) {
      toast.error('Veuillez sélectionner une méthode de paiement');
      return;
    }
    if (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && !paymentProofFile) {
      toast.error('Veuillez ajouter une preuve de paiement Mobile Money');
      return;
    }

    submitLockRef.current = true;
    setProcessing(true);
    try {
      let deliveryId = createdDeliveryIdRef.current;

      if (!deliveryId) {
        const deliveryResponse = await axios.post(`${API}/deliveries`, {
          client_info: {
            full_name: formData.client_name,
            phone: formData.client_phone,
            address: formData.client_address,
            district: formData.client_district,
            city_code: formData.client_city,
            reference: formData.delivery_reference || undefined,
          },
          pickup_address: formData.pickup_address,
          pickup_reference: formData.pickup_reference || undefined,
          pickup_district: formData.pickup_district,
          pickup_city: formData.pickup_city,
          delivery_address: formData.client_address,
          delivery_reference: formData.delivery_reference || undefined,
          delivery_district: formData.client_district,
          delivery_city: formData.client_city,
          weight: Number(effectiveDeliveryWeight),
          description: formData.description,
          items: buildDeliveryItemsPayload(formData.items),
          declared_value: effectiveDeclaredValue,
          service_tier: formData.service_tier || 'standard',
          is_insured: Boolean(formData.is_insured),
          insured_value: formData.is_insured && formData.insured_value ? parseFloat(formData.insured_value) : undefined,
          duplicate_source_delivery_id: formData.duplicate_source_delivery_id || undefined,
          promo_code: promoValidation?.valid ? normalizePromoCodeInput(promoValidation?.promo_code?.code || promoCodeInput) : undefined,
          idempotency_key: deliveryCreateIdempotencyKeyRef.current,
        });

        deliveryId = extractCreateDeliveryId(deliveryResponse.data || {});
        if (!deliveryId) {
          throw new Error('INVALID_DELIVERY_RESPONSE');
        }
        createdDeliveryIdRef.current = deliveryId;
      }

      let proofFileId;
      if (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod)) {
        const uploadedProof = await uploadPaymentProof({
          file: paymentProofFile,
          context: 'delivery_payment',
          deliveryId,
        });
        proofFileId = uploadedProof?.id;
      }

      const paymentResponse = await axios.post(`${API}/payments`, null, {
        params: {
          delivery_id: deliveryId,
          amount: estimatedCost.amount,
          currency: estimatedCost.currency,
          method: paymentMethod,
          transaction_id: transactionId.trim() || undefined,
          proof_file_id: proofFileId,
        },
      });

      const paymentPayload = paymentResponse.data || {};
      const defaultSuccessMessage = paymentMethod === 'wallet'
        ? (isAlreadyProcessedMutation(paymentPayload)
          ? 'Paiement portefeuille déjà  confirmé avec succès.'
          : 'Paiement portefeuille confirmé avec succès.')
        : 'Preuve de paiement envoyee. Livraison en attente de validation admin.';

      toast.success(
        resolvePaymentMutationMessage(
          paymentPayload,
          defaultSuccessMessage
        )
      );

      if (savePickupAddress && formData.pickup_address && formData.pickup_city && formData.pickup_district) {
        try {
          await axios.post(`${API}/user/addresses`, {
            label: pickupAddressLabel.trim() || 'Adresse de ramassage',
            address: formData.pickup_address,
            reference: formData.pickup_reference || undefined,
            district: formData.pickup_district,
            city: formData.pickup_city,
            phone: viewerProfile?.phone || undefined,
            contact_name: viewerProfile?.full_name || undefined,
            is_default: savePickupAsDefault,
            address_type: 'pickup',
          });
          await loadSavedAddresses();
          toast.success('Adresse de ramassage enregistree.');
        } catch (addressError) {
          const detail = addressError.response?.data?.detail;
          if (detail === 'ADDRESS_ALREADY_EXISTS') {
            toast.success('Cette adresse de ramassage est deja enregistree.');
          } else {
            console.error(addressError);
            toast.error("La livraison a ete creee, mais l'adresse n'a pas pu etre enregistree.");
          }
        }
      }

      await loadWalletSummary();
      setShowPaymentDialog(false);
      setPaymentMethod('');
      setTransactionId('');
      setPaymentProofFile(null);
      setSavePickupAddress(false);
      setSavePickupAsDefault(false);
      setPickupAddressLabel('');
      if (onSuccess) {
        try {
          await Promise.resolve(onSuccess());
        } catch (refreshError) {
          console.warn('Delivery success refresh failed:', refreshError);
        }
      }
      createdDeliveryIdRef.current = '';
      deliveryCreateIdempotencyKeyRef.current = buildDeliveryCreateIdempotencyKey();
      navigate(DELIVERY_HISTORY_ROUTE);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'INSUFFICIENT_BALANCE') {
        toast.error('Solde du portefeuille insuffisant pour confirmer cette livraison');
      } else if (isDeliveryPaymentProcessingDetail(detail)) {
        toast.success('Cette livraison est deja en cours de traitement. Vous pouvez la retrouver dans votre historique.');
        setShowPaymentDialog(false);
        navigate(DELIVERY_HISTORY_ROUTE);
      } else if (isDeliveryPaymentAlreadyHandledDetail(detail)) {
        toast.success('Cette livraison a déjà  été prise en compte.');
        setShowPaymentDialog(false);
        navigate(DELIVERY_HISTORY_ROUTE);
      } else if (error.message === 'INVALID_DELIVERY_RESPONSE') {
        toast.error("La livraison a ete creee, mais la reponse backend est incomplete. Consultez l'historique pour verifier l'etat.");
      } else {
        toast.error(resolveCreateDeliveryFailureMessage({ detail, hasCreatedDeliveryId: Boolean(createdDeliveryIdRef.current) }));
      }
    } finally {
      setProcessing(false);
      submitLockRef.current = false;
    }
  };

  const isProSeller = Boolean(viewerEntitlements?.is_pro_active || viewerEntitlements?.isProActive);
  const proStatusKnown = viewerEntitlements !== null;

  return (
    <div className="mx-auto max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl gradient-text">Nouvelle livraison</CardTitle>
          <CardDescription>Créez une commande de livraison pour votre client</CardDescription>
        </CardHeader>
        <CardContent>
          {duplicationContext && (
            <div
              data-testid="delivery-duplicate-banner"
              className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <p className="font-semibold">Commande dupliquee</p>
              <p className="mt-1">
                Base: {duplicationContext.source_delivery?.tracking_code || duplicationContext.source_delivery?.id || duplicationContext.sourceId}. Modifiez les informations du nouveau client avant de continuer.
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                <Package className="h-5 w-5 text-red-600" />
                Informations du client final
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="client_name">Nom complet *</Label>
                  <Input id="client_name" name="client_name" data-testid="client-name-input" value={formData.client_name} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label htmlFor="client_phone">Téléphone *</Label>
                  <Input id="client_phone" name="client_phone" data-testid="client-phone-input" placeholder="+242..." value={formData.client_phone} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Ville de livraison *</Label>
                  <Select name="client_city" value={formData.client_city} onValueChange={(value) => handleSelectChange('client_city', value)}>
                    <SelectTrigger data-testid="client-city-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      {allCities.map((city) => (
                        <SelectItem key={city.code} value={city.code}>{city.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Arrondissement *</Label>
                  <Select name="client_district" value={formData.client_district} onValueChange={(value) => handleSelectChange('client_district', value)} disabled={!formData.client_city}>
                    <SelectTrigger data-testid="client-district-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      {getDistrictsForCity(formData.client_city).map((district) => (
                        <SelectItem key={district} value={district}>{district}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="client_address">Adresse complète *</Label>
                  <Input id="client_address" name="client_address" data-testid="client-address-input" placeholder="Numéro, rue, quartier..." value={formData.client_address} onChange={handleInputChange} required />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="delivery_reference">Référence</Label>
                  <Input
                    id="delivery_reference"
                    name="delivery_reference"
                    data-testid="delivery-reference-input"
                    placeholder="Ex. Pharmacie X, portail noir, derrière l’église"
                    value={formData.delivery_reference}
                    onChange={handleInputChange}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Repère terrain utile pour aider le livreur à localiser le client.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                <ArrowRight className="h-5 w-5 text-cyan-600" />
                Point de ramassage
              </h3>
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div className="flex-1">
                    <Label>Adresse enregistrée</Label>
                    <Select
                      value={selectedSavedAddressId || '__manual__'}
                      onValueChange={(value) => {
                        if (value === '__manual__') {
                          setSelectedSavedAddressId('');
                          return;
                        }
                        const matchedAddress = savedAddresses.find((item) => item.id === value);
                        applySavedPickupAddress(matchedAddress);
                      }}
                    >
                      <SelectTrigger data-testid="saved-pickup-address-select">
                        <SelectValue placeholder={savedAddressesLoading ? 'Chargement...' : 'Choisissez une adresse enregistrée'} />
                      </SelectTrigger>
                      <SelectContent className="z-[9999]">
                        <SelectItem value="__manual__">Saisie manuelle</SelectItem>
                        {savedAddresses.map((address) => (
                          <SelectItem key={address.id} value={address.id}>
                            {formatSavedAddressLabel(address)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" onClick={() => navigate('/account/addresses')}>
                    Gérer mes adresses
                  </Button>
                </div>
                {savedAddresses.length > 0 && (
                  <p className="mt-2 text-xs text-slate-600">
                    L’adresse par défaut est proposée automatiquement sur une nouvelle commande.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>Ville de ramassage *</Label>
                  <Select name="pickup_city" value={formData.pickup_city} onValueChange={(value) => handleSelectChange('pickup_city', value)}>
                    <SelectTrigger data-testid="pickup-city-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      {allCities.map((city) => (
                        <SelectItem key={city.code} value={city.code}>{city.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Arrondissement *</Label>
                  <Select name="pickup_district" value={formData.pickup_district} onValueChange={(value) => handleSelectChange('pickup_district', value)} disabled={!formData.pickup_city}>
                    <SelectTrigger data-testid="pickup-district-select">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]">
                      {getDistrictsForCity(formData.pickup_city).map((district) => (
                        <SelectItem key={district} value={district}>{district}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="pickup_address">Adresse de ramassage *</Label>
                  <Input id="pickup_address" name="pickup_address" data-testid="pickup-address-input" placeholder="Votre adresse ou celle de l'expéditeur" value={formData.pickup_address} onChange={handleInputChange} required />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="pickup_reference">Référence</Label>
                  <Input
                    id="pickup_reference"
                    name="pickup_reference"
                    data-testid="pickup-reference-input"
                    placeholder="Ex. Pharmacie X, portail noir, derrière l’église"
                    value={formData.pickup_reference}
                    onChange={handleInputChange}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Repère de ramassage si l’adresse de la boutique ou du point de départ manque de précision.
                  </p>
                </div>
                <div className="md:col-span-2 rounded-lg border border-dashed border-slate-300 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="save_pickup_address"
                      checked={savePickupAddress}
                      onCheckedChange={(checked) => setSavePickupAddress(checked === true)}
                    />
                    <div className="space-y-1">
                      <Label htmlFor="save_pickup_address" className="cursor-pointer text-sm font-medium">
                        Enregistrer cette adresse
                      </Label>
                      <p className="text-xs text-slate-600">
                        Utile pour réutiliser rapidement votre point de ramassage sur les prochaines livraisons.
                      </p>
                    </div>
                  </div>

                  {savePickupAddress && (
                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor="pickup_address_label">Libellé</Label>
                        <Input
                          id="pickup_address_label"
                          data-testid="pickup-address-label-input"
                          placeholder="Boutique principale"
                          value={pickupAddressLabel}
                          onChange={(event) => setPickupAddressLabel(event.target.value)}
                        />
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Checkbox
                          id="pickup_address_default"
                          checked={savePickupAsDefault}
                          onCheckedChange={(checked) => setSavePickupAsDefault(checked === true)}
                        />
                        <Label htmlFor="pickup_address_default" className="cursor-pointer text-sm font-medium">
                          Définir comme adresse par défaut
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="mb-4 text-lg font-semibold">Détails du colis</h3>
              <div className="space-y-5" data-testid="delivery-items-section">
                <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Produits</p>
                    <p className="text-sm text-slate-600">
                      Ajoutez vos articles ligne par ligne pour preparer une commande plus claire et plus exploitable.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="add-product-row-btn"
                    onClick={handleAddProductRow}
                    className="w-full md:w-auto"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter un produit
                  </Button>
                </div>

                <div className="space-y-4">
                  {productSummary.rows.map((item, index) => (
                    <div
                      key={item.row_id}
                      data-testid={`delivery-item-row-${index}`}
                      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                        <div className="lg:col-span-4">
                          <Label htmlFor={`delivery-item-title-${item.row_id}`}>Nom du produit</Label>
                          <Input
                            id={`delivery-item-title-${item.row_id}`}
                            data-testid={`delivery-item-title-${index}`}
                            placeholder="Ex: Robe Wax"
                            value={item.title}
                            onChange={(event) => handleItemChange(item.row_id, 'title', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <Label htmlFor={`delivery-item-sku-${item.row_id}`}>SKU</Label>
                          <Input
                            id={`delivery-item-sku-${item.row_id}`}
                            data-testid={`delivery-item-sku-${index}`}
                            placeholder="SKU-001"
                            value={item.sku}
                            onChange={(event) => handleItemChange(item.row_id, 'sku', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <Label htmlFor={`delivery-item-quantity-${item.row_id}`}>Quantite</Label>
                          <Input
                            id={`delivery-item-quantity-${item.row_id}`}
                            data-testid={`delivery-item-quantity-${index}`}
                            type="number"
                            min="1"
                            step="1"
                            value={item.quantity}
                            onChange={(event) => handleItemChange(item.row_id, 'quantity', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <Label htmlFor={`delivery-item-unit-weight-${item.row_id}`}>Poids unitaire (kg)</Label>
                          <Input
                            id={`delivery-item-unit-weight-${item.row_id}`}
                            data-testid={`delivery-item-unit-weight-${index}`}
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.5"
                            value={item.unit_weight}
                            onChange={(event) => handleItemChange(item.row_id, 'unit_weight', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-2">
                          <Label htmlFor={`delivery-item-total-weight-${item.row_id}`}>Poids total</Label>
                          <Input
                            id={`delivery-item-total-weight-${item.row_id}`}
                            data-testid={`delivery-item-total-weight-${index}`}
                            value={item.totalWeight === null ? '' : String(item.totalWeight)}
                            readOnly
                            placeholder="Auto"
                          />
                        </div>
                        <div className="lg:col-span-4">
                          <Label htmlFor={`delivery-item-variant-${item.row_id}`}>Variante / note</Label>
                          <Input
                            id={`delivery-item-variant-${item.row_id}`}
                            data-testid={`delivery-item-variant-${index}`}
                            placeholder="Taille M / Coloris rouge"
                            value={item.variant_title}
                            onChange={(event) => handleItemChange(item.row_id, 'variant_title', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-3">
                          <Label htmlFor={`delivery-item-unit-price-${item.row_id}`}>Valeur unitaire</Label>
                          <Input
                            id={`delivery-item-unit-price-${item.row_id}`}
                            data-testid={`delivery-item-unit-price-${index}`}
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="15000"
                            value={item.unit_price}
                            onChange={(event) => handleItemChange(item.row_id, 'unit_price', event.target.value)}
                          />
                        </div>
                        <div className="lg:col-span-3">
                          <Label htmlFor={`delivery-item-line-total-${item.row_id}`}>Total ligne</Label>
                          <Input
                            id={`delivery-item-line-total-${item.row_id}`}
                            data-testid={`delivery-item-line-total-${index}`}
                            value={item.lineTotal ? String(item.lineTotal) : ''}
                            readOnly
                            placeholder="Auto"
                          />
                        </div>
                        <div className="flex items-end lg:col-span-2">
                          <Button
                            type="button"
                            variant="ghost"
                            data-testid={`remove-product-row-${index}`}
                            onClick={() => handleRemoveProductRow(item.row_id)}
                            className="w-full text-slate-600 hover:text-red-600"
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  data-testid="delivery-items-summary"
                  className="grid grid-cols-2 gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 md:grid-cols-4"
                >
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Articles distincts</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">{productSummary.distinctItemsCount}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Unites</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">{productSummary.totalUnitsCount}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Poids total</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">
                      {productSummary.hasCompleteWeightData ? `${productSummary.totalWeight} kg` : 'A completer'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Valeur declaree</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">
                      {productSummary.totalDeclaredValue ? `${productSummary.totalDeclaredValue.toLocaleString('fr-FR')} FCFA` : 'Non renseignee'}
                    </p>
                  </div>
                </div>

                {hasDetailedItems && !productSummary.hasCompleteWeightData && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Certains produits n ont pas encore de poids unitaire. Le poids total manuel ci-dessous reste utilise pour le calcul final.
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="weight">Poids total du colis (kg) *</Label>
                    <Input
                      id="weight"
                      name="weight"
                      type="number"
                      min="0"
                      step="0.1"
                      data-testid="weight-input"
                      placeholder="Ex: 2.5"
                      value={displayedWeightValue}
                      onChange={handleInputChange}
                      readOnly={usesComputedWeight}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {usesComputedWeight
                        ? 'Calcule automatiquement a partir des lignes produits.'
                        : 'Utilise pour le calcul backend si vous restez en saisie simple ou si certains poids unitaires manquent.'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="text-sm font-medium text-slate-900">Resume du colis</p>
                    <p className="mt-2 text-sm text-slate-600">
                      {hasDetailedItems
                        ? `${productSummary.distinctItemsCount} ligne(s), ${productSummary.totalUnitsCount} unite(s), ${productSummary.totalDeclaredValue.toLocaleString('fr-FR')} FCFA de valeur declaree.`
                        : 'Le mode simple reste disponible : vous pouvez renseigner uniquement le poids total et la description du colis.'}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea id="description" name="description" data-testid="description-input" placeholder="Décrivez le contenu du colis..." value={formData.description} onChange={handleInputChange} rows={3} required />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Options de service</h3>
                {isProSeller && (
                  <Badge className="flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white">
                    <Crown className="h-3 w-3" />
                    PRO
                  </Badge>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Niveau de service</Label>
                  <Select value={formData.service_tier} onValueChange={(value) => handleSelectChange('service_tier', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="express" disabled={proStatusKnown && !isProSeller}>Express (Pro requis)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500">Le prix final est toujours calculé par le backend.</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_insured"
                      checked={Boolean(formData.is_insured)}
                      onCheckedChange={(checked) => {
                        const enabled = checked === true;
                        setFormData((prev) => ({
                          ...prev,
                          is_insured: enabled,
                          insured_value: enabled ? prev.insured_value : '',
                        }));
                        setEstimatedCost(null);
                        resetAppliedPromo();
                      }}
                      disabled={proStatusKnown && !isProSeller}
                    />
                    <Label htmlFor="is_insured" className="flex cursor-pointer items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-cyan-600" />
                      Assurance colis (Pro requis)
                    </Label>
                  </div>

                  {formData.is_insured && (
                    <div>
                      <Label htmlFor="insured_value">Valeur du colis à assurer *</Label>
                      <Input id="insured_value" name="insured_value" type="number" step="0.01" min="0" placeholder="Ex: 50000" value={formData.insured_value} onChange={handleInputChange} />
                      <p className="mt-1 text-xs text-gray-500">Utilisée pour calculer le supplément assurance.</p>
                    </div>
                  )}
                </div>

                {proStatusKnown && !isProSeller && (
                  <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Les options Express et Assurance sont réservées aux vendeurs Pro.
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Estimation du coût</h3>
                <Button type="button" data-testid="calculate-cost-btn" variant="outline" onClick={calculateEstimate} disabled={calculating}>
                  <Calculator className="mr-2 h-4 w-4" />
                  {calculating ? 'Calcul...' : 'Calculer'}
                </Button>
              </div>

              {estimatedCost && (
                <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-cyan-50">
                  <CardContent className="p-6">
                    <div className="space-y-3 text-center">
                      <p className="mb-2 text-sm text-gray-600">Coût estimé</p>
                      <p className="text-4xl font-bold gradient-text">{estimatedCost.amount.toLocaleString()} {estimatedCost.currency}</p>
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {estimatedCost.serviceTier !== 'express' && <Badge variant="outline">Service standard</Badge>}
                        {estimatedCost.serviceTier === 'express' && (
                          <Badge className="flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white">
                            <Crown className="h-3 w-3" />
                            EXPRESS
                          </Badge>
                        )}
                        {estimatedCost.isInsured && (
                          <Badge className="flex items-center gap-1 bg-cyan-600 text-white">
                            <ShieldCheck className="h-3 w-3" />
                            Assurance
                          </Badge>
                        )}
                      </div>
                      {typeof estimatedCost.distanceKm === 'number' && (
                        <p className="mt-2 text-sm text-gray-700">Distance estimée : {estimatedCost.distanceKm.toFixed(2)} km</p>
                      )}
                      {estimatedCost.detailCalcul?.delivery_type && (
                        <p className="mt-1 text-xs text-gray-500">Type : {estimatedCost.detailCalcul.delivery_type === 'inter_city' ? 'Inter-ville' : 'Locale'}</p>
                      )}
                      <DeliveryEstimateSummary
                        source={estimatedCost}
                        testId="delivery-estimate-preview"
                        className="mx-auto max-w-md text-left"
                      />
                      <div className="mx-auto max-w-md rounded-lg border border-red-100 bg-white/80 p-3 text-left">
                        <p className="mb-2 text-xs font-semibold text-gray-600">Détail du calcul</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center justify-between">
                            <span>Transport</span>
                            <span>{Number(estimatedCost.transportAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Supplément express</span>
                            <span>+ {Number(estimatedCost.expressSurchargeAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Supplément assurance</span>
                            <span>+ {Number(estimatedCost.insuranceAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                          </div>
                          {estimatedCost.hasDiscount && (
                            <div className="flex items-center justify-between text-green-700">
                              <span>Code promo{estimatedCost.promoCode ? ` (${estimatedCost.promoCode})` : ''}</span>
                              <span>- {Number(estimatedCost.discountAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                            </div>
                          )}
                          <div className="mt-1 flex items-center justify-between border-t pt-1 font-semibold">
                            <span>Total</span>
                            <span>{Number(estimatedCost.amount || 0).toLocaleString()} {estimatedCost.currency}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mx-auto max-w-md rounded-lg border border-dashed border-gray-300 bg-white/80 p-4 text-left">
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <Label htmlFor="delivery-promo-code">Code promo</Label>
                            <Input
                              id="delivery-promo-code"
                              data-testid="delivery-promo-input"
                              value={promoCodeInput}
                              onChange={(event) => setPromoCodeInput(event.target.value.toUpperCase())}
                              placeholder="PROMO20"
                            />
                          </div>
                          <Button
                            type="button"
                            data-testid="apply-delivery-promo-btn"
                            variant="outline"
                            onClick={handleApplyPromoCode}
                            disabled={promoLoading}
                          >
                            {promoLoading ? 'Application...' : 'Appliquer'}
                          </Button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Le montant final est toujours revalide par le backend avant la creation de la livraison.
                        </p>
                        {promoValidation?.valid && (
                          <p className="mt-2 text-sm text-green-700">
                            {promoValidation?.promo_code?.label || promoValidation?.promo_code?.code} applique.
                          </p>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Calcul basé sur la distance Google Maps et les règles tarifaires actives.</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="flex gap-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => navigate('/vendeur')}>Annuler</Button>
              <Button type="submit" data-testid="create-delivery-submit-btn" className="flex-1 bg-red-500 hover:bg-red-600" disabled={!estimatedCost}>Continuer vers le paiement</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Dialog open={showPaymentDialog} onOpenChange={(nextOpen) => {
        if (!processing) {
          setShowPaymentDialog(nextOpen);
        }
      }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Finaliser le paiement</DialogTitle>
            <DialogDescription className="text-base">Montant à payer : {estimatedCost?.amount?.toLocaleString()} {estimatedCost?.currency}</DialogDescription>
          </DialogHeader>

          {estimatedCost && (
            <Card className="border border-gray-200">
              <CardContent className="space-y-2 p-4 text-sm">
                <DeliveryEstimateSummary
                  source={estimatedCost}
                  variant="compact"
                  testId="delivery-estimate-payment-dialog"
                />
                <div className="flex flex-wrap items-center gap-2">
                  {estimatedCost.serviceTier !== 'express' && <Badge variant="outline">Service standard</Badge>}
                  {estimatedCost.isInsured && <Badge className="bg-cyan-600 text-white">Assurance</Badge>}
                </div>
                <div className="flex items-center justify-between">
                  <span>Transport</span>
                  <span>{Number(estimatedCost.transportAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Supplément express</span>
                  <span>+ {Number(estimatedCost.expressSurchargeAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Supplément assurance</span>
                  <span>+ {Number(estimatedCost.insuranceAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                </div>
                {estimatedCost.hasDiscount && (
                  <>
                    <div className="flex items-center justify-between">
                      <span>Sous-total brut</span>
                      <span>{Number(estimatedCost.amountBeforeDiscount || 0).toLocaleString()} {estimatedCost.currency}</span>
                    </div>
                    <div className="flex items-center justify-between text-green-700">
                      <span>Code promo{estimatedCost.promoCode ? ` (${estimatedCost.promoCode})` : ''}</span>
                      <span>- {Number(estimatedCost.discountAmount || 0).toLocaleString()} {estimatedCost.currency}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between border-t pt-2 font-semibold">
                  <span>Total</span>
                  <span>{Number(estimatedCost.amount || 0).toLocaleString()} {estimatedCost.currency}</span>
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="mobile-money" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="mobile-money" className="flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Mobile Money
              </TabsTrigger>
              <TabsTrigger value="wallet" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Portefeuille
              </TabsTrigger>
            </TabsList>

            <TabsContent value="mobile-money" className="mt-6">
              <MobileMoneyPayment
                amount={estimatedCost?.amount || 0}
                currency={estimatedCost?.currency || 'FCFA'}
                selectedMethod={paymentMethod}
                transactionId={transactionId}
                receiptFile={paymentProofFile}
                onTransactionIdChange={setTransactionId}
                onReceiptFileChange={setPaymentProofFile}
                onPaymentMethodSelect={(method) => {
                  setPaymentMethod(method);
                  setTransactionId('');
                  setPaymentProofFile(null);
                }}
              />
            </TabsContent>

            <TabsContent value="wallet" className="mt-6">
              <Card className="border-2 border-cyan-200 bg-cyan-50">
                <CardContent className="space-y-3 p-6">
                  <h3 className="text-lg font-semibold">Paiement via le portefeuille Coli Noki</h3>
                  <p className="text-sm text-gray-700">Paiement immédiat si votre solde est suffisant.</p>
                  <p className="text-sm">Solde actuel : <span className="font-semibold">{Number(walletSummary?.balance || 0).toLocaleString()} {walletSummary?.currency || estimatedCost?.currency}</span></p>
                  {estimatedCost?.hasDiscount && (
                    <p className="text-sm text-green-700">
                      Code promo applique : - {Number(estimatedCost.discountAmount || 0).toLocaleString()} {estimatedCost.currency}
                    </p>
                  )}
                    <Button type="button" variant={paymentMethod === 'wallet' ? 'default' : 'outline'} className={paymentMethod === 'wallet' ? 'bg-cyan-600 hover:bg-cyan-700' : ''} onClick={() => { setPaymentMethod('wallet'); setTransactionId(''); setPaymentProofFile(null); }}>
                      Utiliser mon portefeuille
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)} disabled={processing}>Annuler</Button>
            <Button type="button" className="bg-red-500 hover:bg-red-600" onClick={handleCreateDelivery} disabled={processing || !paymentMethod || (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && !paymentProofFile)}>
              {processing ? (<><div className="spinner mr-2"></div>Traitement...</>) : 'Confirmer la commande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateDelivery;
